import gql from "graphql-tag.macro";

const loadUserQuery = gql`
  query userById($id: ID!) {
    userById(id: $id) {
      _id
      firstname
      lastname
      email
      avatar
      userContext {
        level
        position
        strengths
      }
      groups {
        ... on ProjectGroup {
          name
          id
          displayName
          hasChildGroups
          path
          created
        }
      }
    }
  }
`;

const loadAllUsersQuery = gql`
  query loadAllUsersInProject {
    currentProject {
      ... on Project {
        _id
        availableusers {
          edges {
            node {
              _id
              firstname
              lastname
              email
              avatar
              userContext {
                level
                position
                strengths
              }
              roles {
                id
                name
              }
              groups {
                ... on ProjectGroup {
                  name
                  id
                  displayName
                  hasChildGroups
                  path
                  created
                  subGroups {
                    id
                  }
                }
              }
            }
          }
        }
        groups {
          id
          name
          description
          lastUpdated
          members {
            _id
            firstname
            lastname
          }
          hasRoles
          displayName
          hasChildGroups
          path
          created
        }
      }
    }
  }
`;

const loadAssignableUsersQuery = gql`
  query LoadAssignableUsersQuery {
    LoadAssignableUsersQuery {
      _id
      firstname
      lastname
      email
      avatar
      groups {
        ... on ProjectGroup {
          name
          id
          displayName
          hasChildGroups
          path
          created
        }
      }
    }
  }
`;

const createUserMutation = gql`
  mutation createUserInProject(
    $newUser: CreateUserInput!
    $proceedToApplicationLink: URL
    $userGroups: [String!]!
    $passive: Boolean
  ) {
    createUserInProject(
      newUser: $newUser
      proceedToApplicationLink: $proceedToApplicationLink
      userGroups: $userGroups
      passive: $passive
    ) {
      user {
        _id
        firstname
        lastname
        email
        avatar
        groups {
          ... on ProjectGroup {
            name
            id
            displayName
            hasChildGroups
            path
            created
          }
        }
      }
    }
  }
`;

const updateUserMutation = gql`
  mutation editUser($modifications: ModifyUserInput!, $userId: ID!) {
    editUser(modifications: $modifications, userId: $userId) {
      _id
      firstname
      lastname
      email
      avatar
      userContext {
        level
        position
        strengths
      }
      groups {
        ... on ProjectGroup {
          name
          id
          hasChildGroups
          path
          created
        }
      }
    }
  }
`;

const sendVerificationMutation = gql`
  mutation sendVerification($userid: ID!) {
    sendVerification(userid: $userid) {
      statusCode
      success
    }
  }
`;

const changeStatusUserMutation = gql`
  mutation changeStatusUser(
    $addUserGroups: [String!]!
    $removeUserGroups: [String!]!
    $userId: ID!
    $passive: Boolean
  ) {
    modifyUserProjectRole(
      userId: $userId
      addUserGroups: $addUserGroups
      removeUserGroups: $removeUserGroups
      passive: $passive
    ) {
      user {
        _id
        firstname
        lastname
        email
        avatar
        groups {
          ... on ProjectGroup {
            name
            id
            displayName
            hasChildGroups
            path
            created
          }
        }
      }
    }
  }
`;

const changeUsersStatusMutation = gql`
  mutation changeStatusUsers($input: [MoveUserDefinition!]!) {
    modifyUsersProjectRole(input: $input) {
      user {
        _id
        firstname
        lastname
        email
        avatar
        groups {
          ... on ProjectGroup {
            name
            id
            displayName
            hasChildGroups
            path
            created
          }
        }
      }
    }
  }
`;

export const loadSingleUser = async ({ id }, { client }) => {
  const {
    data: { userById: result },
    errors,
  } = await client.query({
    query: loadUserQuery,
    variables: { id },
  });
  return { result, errors };
};

export const loadAllUsers = async ({ client }) => {
  const {
    data: {
      currentProject: {
        availableusers: { edges },
        groups,
      },
    },
    errors,
  } = await client.query({
    query: loadAllUsersQuery,
    fetchPolicy: "no-cache"
  });
  return {
    result: { users: edges?.map(({ node }) => node) || [], groups },
    errors,
  };
};

export const loadUserAssignableToWorkflow = async (
  { entityId, entityType },
  { client }
) => {
  const { data } = await client.query({
    query: loadAssignableUsersQuery,
    variables: { payload: { entityId, entityType } },
  });
  return data;
};

export const createUser = async (
  { newUser: { firstname, lastname, email }, role, passive },
  { client }
) => {
  const {
    data: {
      createUserInProject: { user },
    },
    errors,
  } = await client.mutate({
    mutation: createUserMutation,
    variables: {
      newUser: { firstName: firstname, lastName: lastname, email },
      proceedToApplicationLink: window.location.origin,
      userGroups: [role],
      passive,
    },
  });
  return { user, errors };
};

export const updateUser = async (
  { firstname, lastname, id, strengths },
  { client }
) => {
  const modifications = {
    firstName: firstname,
    lastName: lastname,
    strengths: strengths || [],
  };
  const {
    data: { editUser: result },
    errors,
  } = await client.mutate({
    mutation: updateUserMutation,
    variables: {
      modifications,
      userId: id,
    },
    fetchPolicy: "no-cache",
  });
  return { result, errors };
};

export const changeUserStatus = async (
  { userId, addUserGroups, removeUserGroups, passive = true },
  { client }
) => {
  const {
    data: { modifyUserProjectRole: result },
    errors,
  } = await client.mutate({
    mutation: changeStatusUserMutation,
    variables: {
      addUserGroups,
      removeUserGroups,
      passive,
      userId,
      addUserRoles: [],
      removeUserRoles: [],
    },
  });
  return { result, errors };
};

export const changeUsersStatus = async ({ usersToRemove }, { client }) => {
  const {
    data: { modifyUsersProjectRole: result },
    errors,
  } = await client.mutate({
    mutation: changeUsersStatusMutation,
    variables: {
      input: usersToRemove,
    },
  });
  return { result, errors };
};
export const sendVerification = async ({ userid }, { client }) => {
  const {
    data: { sendVerification: result },
    errors,
  } = await client.mutate({
    mutation: sendVerificationMutation,
    variables: {
      userid,
    },
  });
  return { result, errors };
};
